import * as React from 'react';
import makeAsyncScriptLoader from 'react-async-script';
import type { Props } from './types';

export class ReCAPTCHA extends React.Component<Props> {
	_widgetId: number;
	_executeRequested: boolean = false;
	captcha: HTMLDivElement;
	executionResolve?: (value: string | null) => void;
	executionReject?: (reason?: string | null) => void;
	static defaultProps = {
		onChange: () => 0,
		theme: 'light',
		type: 'image',
		tabindex: 0,
		size: 'normal',
		badge: 'bottomright',
	};
	constructor(props: Props) {
		super(props);
		// Suppressing existing violation. Please fix this.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		this.handleExpired = this.handleExpired.bind(this);
		// Suppressing existing violation. Please fix this.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		this.handleErrored = this.handleErrored.bind(this);
		// Suppressing existing violation. Please fix this.
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
		this.handleRecaptchaRef = this.handleRecaptchaRef.bind(this);
	}

	getValue() {
		if (this.props.grecaptcha && this.props.grecaptcha.enterprise && this._widgetId !== undefined) {
			return this.props.grecaptcha.enterprise.getResponse(this._widgetId);
		}
		return null;
	}

	getWidgetId() {
		if (this.props.grecaptcha && this._widgetId !== undefined) {
			return this._widgetId;
		}
		return null;
	}

	execute() {
		const { grecaptcha } = this.props;

		if (grecaptcha && grecaptcha.enterprise && this._widgetId !== undefined) {
			return grecaptcha.enterprise.execute(this._widgetId);
		}
		this._executeRequested = true;
		return null;
	}

	reset() {
		if (this.props.grecaptcha && this.props.grecaptcha.enterprise && this._widgetId !== undefined) {
			this.props.grecaptcha.enterprise.reset(this._widgetId);
		}
	}

	handleExpired() {
		if (this.props.onExpired) {
			this.props.onExpired();
		} else if (this.props.onChange) {
			/*
                we will be resetting the recaptcha after the faulty token expires.
                The expiry will happen once the current token has returned a 400 from the JIRA backend,
                which will keep the page on the same route and not navigate further and thus lead to token
                expiry from google which is around 2mins.
                In case of a success, the route changes so this condition is out of question.
            */
			this.reset();
		}
	}

	handleErrored() {
		if (this.props.onErrored) this.props.onErrored();
	}

	explicitRender() {
		if (
			this.props.grecaptcha &&
			this.props.grecaptcha.enterprise &&
			this.props.grecaptcha.enterprise.render &&
			this._widgetId === undefined
		) {
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			const wrapper = document.createElement('div');
			this._widgetId = this.props.grecaptcha.enterprise.render(wrapper, {
				sitekey: this.props.sitekey,
				callback: this.props.onChange,
				theme: this.props.theme,
				type: this.props.type,
				tabindex: this.props.tabindex,
				'expired-callback': this.handleExpired,
				'error-callback': this.handleErrored,
				size: this.props.size,
				stoken: this.props.stoken,
				hl: this.props.hl,
				badge: this.props.badge,
			});
			this.captcha.appendChild(wrapper);
		}
		if (this._executeRequested && this.props.grecaptcha && this._widgetId !== undefined) {
			this._executeRequested = false;
			this.execute();
		}
	}

	componentDidMount() {
		this.explicitRender();
	}

	componentDidUpdate() {
		this.explicitRender();
	}

	componentWillUnmount() {
		if (this._widgetId !== undefined) {
			this.delayOfCaptchaIframeRemoving();
			this.reset();
		}
	}

	delayOfCaptchaIframeRemoving() {
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		const temporaryNode = document.createElement('div');
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
		document.body.appendChild(temporaryNode);
		temporaryNode.style.display = 'none';

		// move of the recaptcha to a temporary node
		while (this.captcha.firstChild) {
			temporaryNode.appendChild(this.captcha.firstChild);
		}

		// delete the temporary node after reset will be done
		setTimeout(() => {
			// Upgrading @atlaskit/platform; fix these violations.
			// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage
			document.body.removeChild(temporaryNode);
		}, 5000);
	}

	handleRecaptchaRef(elem: HTMLDivElement) {
		this.captcha = elem;
	}

	render() {
		// consume properties owned by the reCATPCHA, pass the rest to the div so the user can style it.
		const {
			sitekey,
			onChange,
			theme,
			type,
			tabindex,
			onExpired,
			onErrored,
			size,
			stoken,
			grecaptcha,
			badge,
			hl,
			...childProps
		} = this.props;

		return <div {...childProps} ref={this.handleRecaptchaRef} />;
	}
}

const callbackName = 'onloadcallback';
const globalName = 'grecaptcha';
const recaptchaOptions = 'recaptchaOptions';
// @ts-ignore TS(7015) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Element implicitly has an 'any' type because index... Remove this comment to see the full error message
// Suppressing existing violation. Please fix this.
// eslint-disable-next-line @typescript-eslint/no-unsafe-return
const getOptions = () => (typeof window !== 'undefined' && window[recaptchaOptions]) || {};

export function getURL() {
	// Suppressing existing violation. Please fix this.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const dynamicOptions = getOptions();
	// TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
	// eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unsafe-member-access
	const lang = dynamicOptions.lang ? `&hl=${dynamicOptions.lang}` : '';
	const hostname = 'www.google.com';
	return `https://${hostname}/recaptcha/enterprise.js?onload=${callbackName}&render=explicit${lang}`;
}

// Suppressing existing violation. Please fix this.
// eslint-disable-next-line @typescript-eslint/no-unsafe-call
export default makeAsyncScriptLoader(getURL, {
	callbackName,
	globalName,

	// Suppressing existing violation. Please fix this.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
	removeOnUnmount: getOptions().removeOnUnmount || false,
})(ReCAPTCHA);
